import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaPrompt } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaPrompt";
import { useTranslation } from "@coworker/locales";
import { useState } from "react";
import styles from "./DeleteTeamTasksPrompt.module.css";

export enum TeamTasksActionOption {
  REASSIGN = "REASSIGN",
  CLOSE = "CLOSE",
}

type DeleteTeamTasksPromptProps = {
  isVisible: boolean;
  onClose: () => void;
  onConfirm: (option: TeamTasksActionOption) => void;
  taskCount: number;
};

export const DeleteTeamTasksPrompt = ({
  isVisible,
  onClose,
  onConfirm,
  taskCount,
}: DeleteTeamTasksPromptProps) => {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState<TeamTasksActionOption>(
    TeamTasksActionOption.REASSIGN
  );

  const handleConfirm = () => {
    onConfirm(selectedOption);
  };

  const handleClose = () => {
    setSelectedOption(TeamTasksActionOption.REASSIGN);
    onClose();
  };

  const title =
    taskCount === 1
      ? t("deleteTeamTasksPromptTitleSingleString")
      : t("deleteTeamTasksPromptTitleString", { count: taskCount });

  return (
    <FixaModal
      visible={isVisible}
      handleCloseBtn={handleClose}
      data-testid="delete-team-tasks-prompt-modal"
    >
      <FixaPrompt
        className={styles["prompt"]}
        titleId="deleteTeamTasksPrompt"
        title={title}
        header={null}
        data-testid="delete-team-tasks-prompt"
        footer={
          <FixaModalFooter className={styles["modal-footer"]}>
            <FixaButton
              type={"secondary"}
              text={t("cancelString")}
              onClick={handleClose}
              data-testid="delete-team-tasks-cancel-button"
            />
            <FixaButton
              type={"primary"}
              text={t("confirmString")}
              onClick={handleConfirm}
              data-testid="delete-team-tasks-confirm-button"
            />
          </FixaModalFooter>
        }
      >
        <p
          className={styles["subtitle"]}
          data-testid="delete-team-tasks-subtitle"
        >
          {t("deleteTeamTasksPromptSubtitleString")}
        </p>

        <FixaListView
          className={styles["list-view"]}
          id="delete-team-tasks-options"
          data-testid="delete-team-tasks-options"
        >
          <FixaListViewItem
            id="reassign-tasks"
            control="radiobutton"
            title={t("deleteTeamTasksPromptReassignOptionString")}
            onChange={() => setSelectedOption(TeamTasksActionOption.REASSIGN)}
            inset
            emphasised
            data-testid="delete-team-tasks-reassign-option"
            controlProps={{
              checked: selectedOption === TeamTasksActionOption.REASSIGN,
              value: TeamTasksActionOption.REASSIGN,
            }}
          />

          <FixaListViewItem
            id="close-tasks"
            control="radiobutton"
            title={
              <span className={styles["warning-title"]}>
                {t("deleteTeamTasksPromptCloseOptionString")}
              </span>
            }
            onChange={() => setSelectedOption(TeamTasksActionOption.CLOSE)}
            inset
            emphasised
            data-testid="delete-team-tasks-close-option"
            controlProps={{
              checked: selectedOption === TeamTasksActionOption.CLOSE,
              value: TeamTasksActionOption.CLOSE,
            }}
          />
        </FixaListView>
      </FixaPrompt>
    </FixaModal>
  );
};
